import { Box, Container, Heading, Image, Spinner, Stack, Text, HStack, Badge, Link, SimpleGrid, Button, AspectRatio, useColorModeValue, Flex, VStack, Tag, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, IconButton, useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { getArticle, getArticleBySlug, getArticleMatchLink, trackArticleView, getArticles } from '../services/articles';
import { articleRead } from '../services/engagement';
import MainLayout from '../components/layout/MainLayout';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import NewsletterCTA from '../components/common/NewsletterCTA';
import EmbeddedPoll from '../components/polls/EmbeddedPoll';
import { ArrowRight, Eye, Clock, SearchX, ChevronLeft, ChevronRight, ExternalLink, Copy, Play } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent as umamiTrackEvent, trackMatchView as umamiTrackMatchView, trackVideoPlay as umamiTrackVideoPlay, trackArticleView as umamiTrackArticleView } from '../utils/umami';
import { assetUrl } from '../utils/url';
import { API_URL } from '../services/api';
import TeamLogo from '../components/common/TeamLogo';
import MatchModal from '../components/home/MatchModal';
import { extractPalette } from '../utils/colors';
import { getTeamLogo } from '../utils/sportLogosAPI';
import { getBanners, Banner as UIBanner } from '../services/banners';
import FilePreview from '../components/common/FilePreview';
import { usePublicSettings } from '../hooks/usePublicSettings';
import InstagramGeneratorButton from '../components/admin/InstagramGeneratorButton';
import { MatchSnapshot } from '../services/instagram';
import { Widget } from '../components/widgets/Widget';
import { MatchesWidget } from '../components/widgets/MatchesWidget';
import { getCompetitionAliasesPublic, CompetitionAlias } from '../services/competitionAliases';
import { getUpcomingEvents } from '../services/eventService';
import CommentsSection from '../components/comments/CommentsSection';
import { startOfMonth, startOfWeek, addDays, isSameMonth, format, addMonths } from 'date-fns';
import { cs } from 'date-fns/locale';

const toText = (html?: string) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
};

const ArticleDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['article', slug ? `slug:${slug}` : `id:${id}`],
    queryFn: () => (slug ? getArticleBySlug(slug!) : getArticle(id!)),
    enabled: Boolean(slug || id),
  });

  

  
  
  // Load competition aliases to resolve category → alias mapping for MatchesWidget filtering
  const aliasesQ = useQuery<{ list: CompetitionAlias[] }>({
    queryKey: ['competition-aliases-public'],
    queryFn: async () => {
      try {
        const list = await getCompetitionAliasesPublic();
        return { list };
      } catch {
        return { list: [] as CompetitionAlias[] };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const normalize = (s?: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const resolveAliasName = React.useCallback((compName?: string): string => {
    const name = String(compName || '');
    const normComp = normalize(name);
    const list = aliasesQ.data?.list || [];
    for (const a of list) {
      const aAlias = normalize(a.alias);
      const aOrig = normalize(a.original_name || '');
      if (aOrig && (normComp.includes(aOrig) || aOrig.includes(normComp))) return a.alias;
      if (aAlias && (normComp.includes(aAlias) || aAlias.includes(normComp))) return a.alias;
    }
    return name;
  }, [aliasesQ.data?.list]);

  // Determine which category name to use for MatchesWidget (prefer backend-provided competition_alias)
  const categoryNameForMatches = React.useMemo(() => {
    const fromBackend = (data as any)?.competition_alias;
    if (fromBackend) return fromBackend as string;
    const cat = (data as any)?.category?.name as string | undefined;
    if (!cat) return undefined;
    return resolveAliasName(cat);
  }, [(data as any)?.competition_alias, (data as any)?.category?.name, resolveAliasName]);

  
  // UI colors and public settings
  const { data: publicSettings } = usePublicSettings();
  const cardBg = useColorModeValue('white','gray.900');
  const videoBg = useColorModeValue('gray.50','gray.800');
  const textMuted = useColorModeValue('gray.600','gray.400');
  // Hoist all color mode values to top-level to avoid conditional hook calls
  const videoTitleColor = useColorModeValue('gray.700','gray.300');
  const galleryBg = useColorModeValue('gray.50','gray.800');
  const galleryBorder = useColorModeValue('gray.200','gray.700');
  const attachmentsBg = useColorModeValue('gray.50','gray.800');
  // Mini calendar colors
  const miniTodayBg = useColorModeValue('blue.50','blue.900');
  const miniHoverBg = useColorModeValue('gray.50','gray.700');
  const youtubeCommentsBg = useColorModeValue('white','gray.900');

  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
  const [lightboxImages, setLightboxImages] = React.useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const toast = useToast();
  const [shuffleTick, setShuffleTick] = React.useState(0);

  // Derive opponent colors (for right edge fade) from team logo
  const [opponentColors, setOpponentColors] = React.useState<string[] | null>(null);
  const [isMatchModalOpen, setIsMatchModalOpen] = React.useState(false);
  const [selectedMatch, setSelectedMatch] = React.useState<any>(null);
  const [isYoutubeOpen, setIsYoutubeOpen] = React.useState(false);

  // Placeholders; moved tracking effects below to avoid using variables before declaration

  // Track article view when data is loaded
  React.useEffect(() => {
    if (data && (data as any).id) {
      trackArticleView((data as any).id);
      // Umami + backend generic event
      umamiTrackArticleView((data as any).id, (data as any).title);
    }
  }, [data]);

  // Ensure unified link: if opened by numeric ID and slug exists, redirect to /news/:slug
  React.useEffect(() => {
    if (!id) return;
    try {
      const s = (data as any)?.slug;
      if (s && typeof s === 'string') {
        const target = `/news/${s}`;
        const cur = (typeof window !== 'undefined') ? (window.location.pathname + (window.location.search || '')) : '';
        if (cur && !cur.startsWith(target)) {
          navigate(target, { replace: true });
        }
      }
    } catch {}
  }, [id, (data as any)?.slug, navigate]);

  // Award engagement for article read after 15s dwell (once per article)
  React.useEffect(() => {
    const aid = (data as any)?.id;
    if (!aid) return;
    let timer: any;
    const key = `fc_ar_read_${aid}`;
    const already = (() => { try { return localStorage.getItem(key) === '1'; } catch { return false; } })();
    if (!already) {
      timer = setTimeout(async () => {
        try {
          await articleRead(Number(aid));
          try { localStorage.setItem(key, '1'); } catch {}
        } catch {}
      }, 15000);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [(data as any)?.id]);

  // Delegated click tracking for normal links inside content
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  // Transform content to ensure /uploads URLs resolve against API origin and allow iframes
  // Memoize the transformation function to prevent infinite loops
  const toAbsoluteUploads = React.useCallback((html?: string) => {
    if (!html) return '';
    try {
      const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
      // Replace src="/uploads... and href="/uploads...
      return html
        .replace(/src=("|')\s*(\/uploads\/[^"']+)("')/g, (_m, q1, p2, q3) => `src=${q1}${origin}${p2}${q3}`)
        .replace(/href=("|')\s*(\/uploads\/[^"']+)("')/g, (_m, q1, p2, q3) => `href=${q1}${origin}${p2}${q3}`)
        // Also rewrite absolute localhost/127.0.0.1 uploads links to backend origin
        .replace(/src=("|')\s*https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/uploads\/[^"']+)("')/g, (_m, q1, p2, q3) => `src=${q1}${origin}${p2}${q3}`)
        .replace(/href=("|')\s*https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/uploads\/[^"']+)("')/g, (_m, q1, p2, q3) => `href=${q1}${origin}${p2}${q3}`);
    } catch {
      return html;
    }
  }, []);

  const safeContentHTML = React.useMemo(() => {
    const transformed = toAbsoluteUploads((data as any)?.content);
    return DOMPurify.sanitize(transformed || '', {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['class', 'target', 'rel', 'allow', 'allowfullscreen', 'style', 'data-filters', 'data-img-id', 'data-bullets', 'data-list'],
    });
  }, [(data as any)?.content, toAbsoluteUploads]);

  // Fetch gallery album if article has one (fallback to URL when ID is missing)
  const galleryAlbumQuery = useQuery({
    queryKey: ['article-gallery-album', (data as any)?.gallery_album_id || (data as any)?.gallery_album_url],
    enabled: Boolean((data as any)?.gallery_album_id || (data as any)?.gallery_album_url),
    queryFn: async () => {
      const albumId = (data as any)?.gallery_album_id;
      let photoIds: string[] = [];
      const raw = (data as any)?.gallery_photo_ids;
      if (Array.isArray(raw)) {
        photoIds = raw as string[];
      } else if (typeof raw === 'string') {
        // Try JSON parse, otherwise split by comma
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            photoIds = parsed.map(String);
          } else {
            photoIds = raw.split(',').map(s => s.trim()).filter(Boolean);
          }
        } catch {
          photoIds = raw.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      
      // Resolve backend origin for cached files regardless of frontend origin
      let origin = '';
      try {
        const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || '').trim();
        if (assetBase) {
          origin = new URL(assetBase, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        }
      } catch {}
      if (!origin) {
        try {
          origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
        } catch {}
      }
      
      // Try to find album in both sources
      const [profileRes, albumsRes] = await Promise.allSettled([
        fetch(`${origin}/cache/prefetch/zonerama_profile.json`, { cache: 'no-cache' }),
        fetch(`${origin}/cache/prefetch/zonerama_albums.json`, { cache: 'no-cache' })
      ]);
      
      // Check profile albums first
      if (profileRes.status === 'fulfilled' && profileRes.value.ok) {
        const profileData = await profileRes.value.json();
        const album = (profileData.albums || []).find((a: any) => a.id === albumId);
        if (album) {
          // Use full album photos for the article preview (ignore selected IDs)
          const photos = album.photos;
          return { ...album, photos };
        }
      }
      
      // Check blog albums
      if (albumsRes.status === 'fulfilled' && albumsRes.value.ok) {
        const albumsData = await albumsRes.value.json();
        const blogAlbums = Array.isArray(albumsData) ? albumsData : [];
        const album = blogAlbums.find((a: any) => a.id === albumId);
        if (album) {
          const photos = album.photos;
          return { ...album, photos };
        }
      }
      
      // Fallback: load by URL via proxy endpoint
      const albumUrl = (data as any)?.gallery_album_url;
      if (albumUrl) {
        const params = new URLSearchParams({ link: albumUrl, photo_limit: '12', rendered: 'true' });
        const resp = await fetch(`${API_URL}/zonerama-album?${params.toString()}`);
        if (resp.ok) {
          const payload = await resp.json();
          let photos: any[] = [];
          if (Array.isArray(payload?.albums) && payload.albums.length > 0) {
            photos = payload.albums[0]?.photos || [];
          } else if (Array.isArray(payload?.photos)) {
            photos = payload.photos;
          }
          return { id: albumUrl, title: 'Album', date: '', photos } as any;
        }
      }

      return null;
    },
    staleTime: 60_000,
  });

  // Extract image sources from content HTML
  const contentImageSources: string[] = React.useMemo(() => {
    try {
      const div = document.createElement('div');
      div.innerHTML = safeContentHTML || '';
      const imgs = Array.from(div.querySelectorAll('img')) as HTMLImageElement[];
      const srcs = imgs.map((img) => img.getAttribute('src') || '').filter(Boolean);
      // Deduplicate while preserving order
      const seen = new Set<string>();
      const uniq: string[] = [];
      for (const s of srcs) { if (!seen.has(s)) { seen.add(s); uniq.push(s); } }
      return uniq;
    } catch { return []; }
  }, [safeContentHTML]);

  // Compute gallery selected and extra images to match visible thumbnails
  const gallerySelectedAndExtra = React.useMemo(() => {
    const albumPhotos = Array.isArray(galleryAlbumQuery.data?.photos) ? (galleryAlbumQuery.data?.photos as any[]) : [];
    const rawSel: any = (data as any)?.gallery_photo_ids;
    let selectedIds: string[] = [];
    if (Array.isArray(rawSel)) selectedIds = rawSel.map(String);
    else if (typeof rawSel === 'string' && rawSel.trim() !== '') {
      try { const parsed = JSON.parse(rawSel); if (Array.isArray(parsed)) selectedIds = parsed.map(String); else selectedIds = rawSel.split(',').map((s: string) => s.trim()).filter(Boolean); } catch { selectedIds = rawSel.split(',').map((s: string) => s.trim()).filter(Boolean); }
    }
    const selected = albumPhotos.filter((p: any) => selectedIds.includes(String(p.id)));
    const rest = albumPhotos.filter((p: any) => !selectedIds.includes(String(p.id)));
    // Shuffle only when user opens the lightbox (keeps UI stable otherwise)
    const extra = [...rest]
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.min(5, rest.length));
    return { selected, extra } as { selected: any[]; extra: any[] };
  }, [galleryAlbumQuery.data?.photos, (data as any)?.gallery_photo_ids, shuffleTick]);

  // Build combined image sources used for lightbox navigation (hero + content + gallery thumbs)
  const combinedImageSources: string[] = React.useMemo(() => {
    const hero = (data as any)?.image_url ? (assetUrl((data as any).image_url) || (data as any).image_url) : null;
    const gallerySrcs = [
      ...gallerySelectedAndExtra.selected.map((p: any) => String(p.image_1500 || '')),
      ...gallerySelectedAndExtra.extra.map((p: any) => String(p.image_1500 || '')),
    ].filter(Boolean);
    const all = [
      ...(hero ? [hero] : []),
      ...contentImageSources,
      ...gallerySrcs,
    ];
    const seen = new Set<string>();
    const uniq: string[] = [];
    for (const s of all) { if (s && !seen.has(s)) { seen.add(s); uniq.push(s); } }
    return uniq;
  }, [(data as any)?.image_url, contentImageSources, gallerySelectedAndExtra.selected, gallerySelectedAndExtra.extra]);

  const openLightboxAtSrc = React.useCallback((src: string) => {
    const idx = combinedImageSources.findIndex((s) => s === src);
    const start = idx >= 0 ? idx : 0;
    setLightboxImages(combinedImageSources);
    setLightboxIndex(start);
    setIsLightboxOpen(true);
    // Trigger a gentle shuffle of extra gallery tiles after opening ("accidental feature")
    setShuffleTick((t) => t + 1);
  }, [combinedImageSources]);

  const onPrev = React.useCallback(() => {
    setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
  }, [lightboxImages.length]);
  const onNext = React.useCallback(() => {
    setLightboxIndex((i) => (i + 1) % lightboxImages.length);
  }, [lightboxImages.length]);

  // Handle clicks on images inside the rich content to open lightbox
  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const img = (target.closest ? target.closest('img') : null) as HTMLImageElement | null;
      if (img && img.getAttribute('src')) {
        e.preventDefault();
        const src = img.getAttribute('src') as string;
        openLightboxAtSrc(src);
        return;
      }
      const anchor = (target.closest ? target.closest('a') : null) as HTMLAnchorElement | null;
      if (anchor && anchor.href) {
        try {
          const href = anchor.getAttribute('href') || anchor.href;
          umamiTrackEvent('Link Click', { href, page: window.location.pathname, context: 'article_content' });
        } catch {}
      }
    };
    el.addEventListener('click', handler);
    return () => { el.removeEventListener('click', handler); };
  }, [contentRef.current, openLightboxAtSrc]);

  // Keyboard navigation in lightbox
  React.useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLightboxOpen, onPrev, onNext]);

  // Fetch linked match (public)
  const matchLinkQuery = useQuery({
    queryKey: ['article-match-link', (data as any)?.id],
    queryFn: () => getArticleMatchLink((data as any)?.id),
    enabled: Boolean((data as any)?.id),
    staleTime: 60_000,
  });

  // From cached FACR club info, resolve the match details
  const facrMatchQuery = useQuery({
    queryKey: ['facr-cached-match', (matchLinkQuery.data as any)?.external_match_id],
    enabled: Boolean((matchLinkQuery.data as any)?.external_match_id),
    queryFn: async () => {
      const origin = new URL(API_URL, window.location.origin).origin;
      const url = `${origin}/cache/prefetch/facr_club_info.json`;
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) return null as any;
      const json = await res.json();
      const comps = Array.isArray(json?.competitions) ? json.competitions : [];
      for (const c of comps) {
        const matches = Array.isArray(c.matches) ? c.matches : [];
        for (const m of matches) {
          const mid = String(m.match_id || m.id);
          if (mid === String((matchLinkQuery.data as any)?.external_match_id)) {
            return { ...m, competitionName: c.name };
          }
        }
      }
      return null as any;
    },
    staleTime: 60_000,
  });

  // Derive opponent color (away team) for right-side accent based on logo palette
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m: any = facrMatchQuery?.data as any;
        if (!m) { if (!cancelled) setOpponentColors(null); return; }
        const awayId = String(m?.away_team_id || m?.away_id || '') || undefined;
        const awayName = String(m?.away || m?.away_team || '') || undefined;
        const facrLogo = (m as any)?.away_logo_url as string | undefined;
        const url = await getTeamLogo(awayId, awayName, facrLogo);
        const palette = await extractPalette(url);
        const colors = Array.isArray(palette) && palette.length ? palette.slice(0, 2) : [];
        if (!cancelled) setOpponentColors(colors);
      } catch {
        if (!cancelled) setOpponentColors(null);
      }
    })();
    return () => { cancelled = true; };
  }, [facrMatchQuery?.data]);

  // Track match view when resolved
  React.useEffect(() => {
    const mid = (matchLinkQuery.data as any)?.external_match_id;
    if (mid) {
      umamiTrackMatchView(mid);
    }
  }, [(matchLinkQuery.data as any)?.external_match_id]);

  // Build a snapshot usable for sharing if available (FACR data or article fallback)
  const matchSnapshot: MatchSnapshot | null = React.useMemo(() => {
    const m: any = facrMatchQuery?.data as any;
    if (m) {
      let score = '';
      if (m?.score && m.score !== 'vs') score = String(m.score);
      else if (m?.result_home != null && m?.result_away != null) score = `${m.result_home}:${m.result_away}`;
      return {
        external_match_id: String((matchLinkQuery.data as any)?.external_match_id || ''),
        competition: String(m.competitionName || ''),
        date_time: String(m.date_time || m.date || ''),
        venue: m.venue ? String(m.venue) : undefined,
        home: String(m.home || m.home_team || ''),
        away: String(m.away || m.away_team || ''),
        score,
      };
    }
    const snap: any = (data as any)?.match_snapshot;
    if (snap) {
      return {
        external_match_id: snap.external_match_id,
        competition: snap.competition || snap.competitionName,
        date_time: snap.date_time || snap.date,
        venue: snap.venue,
        home: snap.home,
        away: snap.away,
        score: snap.score,
      } as MatchSnapshot;
    }
    return null;
  }, [facrMatchQuery?.data, (matchLinkQuery.data as any)?.external_match_id, (data as any)?.match_snapshot]);

  // Track gallery section when loaded
  React.useEffect(() => {
    const albumId = (data as any)?.gallery_album_id;
    if (albumId && galleryAlbumQuery.data) {
      umamiTrackEvent('Gallery Section Shown', { album_id: albumId, photos: galleryAlbumQuery.data?.photos?.length || 0 });
    }
  }, [(data as any)?.gallery_album_id, galleryAlbumQuery.data]);

  const articleBannersQ = useQuery<UIBanner[]>({
    queryKey: ['banners', { placement: 'article_inline' }],
    queryFn: () => getBanners({ active: true, placement: 'article_inline' }),
    staleTime: 60 * 1000,
  });
  const articleBanners = (articleBannersQ.data || []) as UIBanner[];
  const sidebarBannersQ = useQuery<UIBanner[]>({
    queryKey: ['banners', { placement: 'article_sidebar' }],
    queryFn: () => getBanners({ active: true, placement: 'article_sidebar' }),
    staleTime: 60 * 1000,
  });
  const sidebarBanners = (sidebarBannersQ.data || []) as UIBanner[];

  const relatedArticlesQuery = useQuery({
    queryKey: ['related-articles', (data as any)?.category?.id || 'none', (data as any)?.id],
    enabled: Boolean((data as any)?.id),
    queryFn: () => getArticles({
      page: 1,
      page_size: 6,
      published: true,
      ...(((data as any)?.category?.id) ? { category_id: (data as any).category.id } : {}),
    }),
    staleTime: 60_000,
  });

  const upcomingEventsQuery = useQuery({
    queryKey: ['upcoming-events-sidebar'],
    queryFn: getUpcomingEvents,
    staleTime: 60_000,
  });

  const latestArticlesQuery = useQuery({
    queryKey: ['latest-articles'],
    queryFn: () => getArticles({ page: 1, page_size: 6, published: true }),
    enabled: Boolean((isError || !data) && (slug || id)),
    staleTime: 60_000,
  });

  // Mini calendar data source (cached FACR club info)
  const allMatchesQ = useQuery({
    queryKey: ['facr-all-matches-mini'],
    queryFn: async () => {
      const origin = new URL(API_URL, window.location.origin).origin;
      const url = `${origin}/cache/prefetch/facr_club_info.json`;
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) return [] as any[];
      const json = await res.json();
      const comps = Array.isArray(json?.competitions) ? json.competitions : [];
      const items: any[] = [];
      for (const c of comps) {
        const matches = Array.isArray(c.matches) ? c.matches : [];
        for (const m of matches) {
          items.push({ ...m, competitionName: c.name });
        }
      }
      return items;
    },
    staleTime: 60_000,
  });

  const catUpcomingMatches = React.useMemo(() => {
    try {
      const list = Array.isArray(allMatchesQ.data) ? (allMatchesQ.data as any[]) : [];
      const now = Date.now();
      const needle = categoryNameForMatches ? normalize(categoryNameForMatches) : '';
      const filtered = list.filter((m: any) => {
        const comp = String(m.competitionName || '');
        const resolved = resolveAliasName(comp);
        const nComp = normalize(comp);
        const nResolved = normalize(resolved);
        const dt: string = String(m.date_time || '');
        const parts = dt.split(' ');
        const [d, t] = [parts[0] || '', parts[1] || ''];
        const [dd, MM, yyyy] = (d || '').split('.');
        const iso = (dd && MM && yyyy) ? `${yyyy}-${String(MM).padStart(2,'0')}-${String(dd).padStart(2,'0')}T${(t || '00:00')}:00` : '';
        const ts = iso ? new Date(iso).getTime() : 0;
        const isFuture = ts > now;
        if (!isFuture) return false;
        if (!needle) return true;
        if (nResolved && nResolved === needle) return true;
        if (nComp === needle) return true;
        return (nResolved && nResolved.includes(needle)) || nComp.includes(needle);
      });
      return filtered;
    } catch { return [] as any[]; }
  }, [allMatchesQ.data, categoryNameForMatches, resolveAliasName]);

  // Mini calendar helpers
  const [miniMonthRef, setMiniMonthRef] = React.useState<Date>(startOfMonth(new Date()));
  const miniWeeks = React.useMemo(() => {
    const start = startOfWeek(startOfMonth(miniMonthRef), { weekStartsOn: 1 });
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) days.push(addDays(start, i));
    return days;
  }, [miniMonthRef]);
  const [miniExpandedKey, setMiniExpandedKey] = React.useState<string | null>(null);
  const facrToDateKey = (dt: string): string => {
    try {
      const [dPart, tPart = '00:00'] = String(dt || '').split(' ');
      const [dd, MM, yyyy] = String(dPart || '').split('.');
      if (dd && MM && yyyy) return `${yyyy}-${String(MM).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
    } catch {}
    return '';
  };
  const miniByDate = React.useMemo(() => {
    const map = new Map<string, any[]>();
    // When no upcoming matches for the article's category, show all matches (like on /kalendar)
    const useAll = !categoryNameForMatches || (catUpcomingMatches || []).length === 0;
    const sourceList: any[] = useAll
      ? (Array.isArray(allMatchesQ.data) ? (allMatchesQ.data as any[]) : [])
      : (catUpcomingMatches || []);
    sourceList.forEach((m: any) => {
      const key = facrToDateKey(String(m.date_time || ''));
      if (!key) return;
      const arr = map.get(key) || [];
      arr.push(m);
      map.set(key, arr);
    });
    Array.from(map.keys()).forEach((k) => {
      const arr = map.get(k) || [];
      arr.sort((a: any, b: any) => {
        const ta = String(a.date_time || '').split(' ')[1] || '00:00';
        const tb = String(b.date_time || '').split(' ')[1] || '00:00';
        return ta.localeCompare(tb);
      });
      map.set(k, arr);
    });
    return map;
  }, [allMatchesQ.data, catUpcomingMatches, categoryNameForMatches]);

  if (isLoading) return <Spinner />;
  if (isError || !data) return (
    <MainLayout>
      <Helmet>
        <title>Článek nenalezen</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container maxW="4xl" py={{ base: 12, md: 16 }}>
        <Stack spacing={6} align="center" textAlign="center">
          <Box color="blue.500">
            <SearchX size={64} />
          </Box>
          <Heading size="xl">Článek nenalezen</Heading>
          <Text color={textMuted} maxW="2xl">
            Je nám líto, ale hledaný článek neexistuje, byl smazán nebo byl přesunut.
          </Text>
          <HStack spacing={3}>
            <Button as={RouterLink} to="/news" colorScheme="blue">Zpět na novinky</Button>
            <Button as={RouterLink} to="/" variant="ghost">Domů</Button>
          </HStack>
        </Stack>

        {Array.isArray((latestArticlesQuery.data as any)?.data) && ((latestArticlesQuery.data as any)?.data?.length || 0) > 0 && (
          <Box mt={12}>
            <Heading as="h2" size="md" mb={4}>Nejnovější články</Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
              {((latestArticlesQuery.data as any).data || []).slice(0, 6).map((a: any) => {
                const link = a.slug ? `/news/${a.slug}` : `/articles/${a.id}`;
                return (
                  <Box
                    key={a.id}
                    as={RouterLink}
                    to={link}
                    borderWidth="1px"
                    borderRadius="lg"
                    p={3}
                    bg={cardBg}
                    _hover={{ textDecoration: 'none', boxShadow: 'md' }}
                  >
                    <Image
                      src={assetUrl(a.image_url) || '/stadium-placeholder.jpg'}
                      alt={a.title}
                      w="100%"
                      h="140px"
                      objectFit="cover"
                      borderRadius="md"
                      mb={2}
                    />
                    <Text fontWeight="600" noOfLines={2}>{a.title}</Text>
                    {a.published_at && (
                      <Text fontSize="sm" color={textMuted}>{new Date(a.published_at).toLocaleDateString('cs-CZ')}</Text>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        )}
      </Container>
    </MainLayout>
  );

  const title = (data as any).seo_title || data.title;
  const description = (data as any).seo_description || toText(data.content).slice(0, 160);
  const ogImageRaw = (data as any).og_image_url || data.image_url || '/logo512.png';
  const ogImage = assetUrl(ogImageRaw) || ogImageRaw;
  const canonical = typeof window !== 'undefined' ? window.location.href : undefined;
  const publishedAt = (data as any).published_at || (data as any).created_at;
  const monthParam = (() => {
    if (!publishedAt) return '';
    try { const d = new Date(publishedAt); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; } catch { return ''; }
  })();

  return (
    <MainLayout>
      <Box>
        <InstagramGeneratorButton
          article={data as any}
          match={matchSnapshot}
          targetUrl={typeof window !== 'undefined' ? window.location.href : undefined}
          placement="fixed"
          size="md"
          align="left"
        />
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={ogImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={ogImage} />
          {canonical && <link rel="canonical" href={canonical} />}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              headline: title,
              image: [ogImage],
              datePublished: (data as any).published_at || data.created_at,
              dateModified: (data as any).updated_at || (data as any).published_at || data.created_at,
              author: data.author ? {
                '@type': 'Person',
                name: `${data.author.first_name || ''} ${data.author.last_name || ''}`.trim() || data.author.email
              } : undefined,
              description,
              mainEntityOfPage: canonical || undefined,
            })}
          </script>
          <script type="application/ld+json">
            {JSON.stringify((() => {
              const items: any[] = [
                { '@type': 'ListItem', position: 1, name: 'Domů', item: (typeof window !== 'undefined' ? window.location.origin : '') + '/' },
                { '@type': 'ListItem', position: 2, name: 'Blog', item: (typeof window !== 'undefined' ? window.location.origin : '') + '/blog' },
              ];
              const catId = (data as any)?.category?.id || (data as any)?.category_id;
              const catName = (data as any)?.category?.name || (data as any)?.category_name;
              if (catName) {
                items.push({
                  '@type': 'ListItem',
                  position: 3,
                  name: String(catName),
                  item: (typeof window !== 'undefined' ? window.location.origin : '') + (catId ? `/blog?category_id=${catId}` : `/blog?category=${encodeURIComponent(String(catName))}`)
                });
              }
              items.push({ '@type': 'ListItem', position: items.length + 1, name: title, item: canonical || undefined });
              return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
            })())}
          </script>
        </Helmet>
        <Box bg="transparent" color="inherit" py={{ base: 6, md: 8 }} mb={2}>
          <Container maxW="7xl">
            <Heading as="h1" size={{ base: 'xl', md: '2xl' }} mb={2}>{data.title}</Heading>
            <HStack spacing={2} rowGap={2} wrap="wrap" fontSize="sm" color={textMuted}>
              {((data as any).read_time || (data as any).estimated_read_minutes) ? (
                <HStack spacing={1}>
                  <Clock size={16} />
                  <Text>{(data as any).read_time || (data as any).estimated_read_minutes} min čtení</Text>
                </HStack>
              ) : null}
              {publishedAt && (
                <Tag as={RouterLink} to={`/blog?month=${monthParam}`} size="sm" variant="subtle">{new Date(publishedAt).toLocaleDateString('cs-CZ')}</Tag>
              )}
              {(data as any)?.category?.id && (
                <Tag as={RouterLink} to={`/blog?category_id=${(data as any).category.id}`} size="sm" variant="subtle">{(data as any).category.name || 'Kategorie'}</Tag>
              )}
              {(matchLinkQuery.data as any)?.external_match_id && (
                <Tag as={RouterLink} to={`/blog?match_id=${(matchLinkQuery.data as any).external_match_id}`} size="sm" variant="subtle">Zápas</Tag>
              )}
              {(data as any).view_count ? (
                <HStack spacing={1} ml={{ base: 0, md: 2 }}>
                  <Eye size={16} />
                  <Text>{(data as any).view_count} zobrazení</Text>
                </HStack>
              ) : null}
            </HStack>
            <Breadcrumb fontSize="sm" mt={2} color={textMuted} separator="/">
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Domů</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/blog">Blog</BreadcrumbLink>
              </BreadcrumbItem>
              {(() => {
                const catId = (data as any)?.category?.id || (data as any)?.category_id;
                const catName = (data as any)?.category?.name || (data as any)?.category_name;
                if (!catName) return null;
                const to = catId ? `/blog?category_id=${catId}` : `/blog?category=${encodeURIComponent(String(catName))}`;
                return (
                  <BreadcrumbItem>
                    <BreadcrumbLink as={RouterLink} to={to}>{String(catName)}</BreadcrumbLink>
                  </BreadcrumbItem>
                );
              })()}
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>{data.title}</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </Container>
        </Box>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, lg: 12 }} spacing={6}>
            <Box gridColumn={{ base: '1 / -1', lg: 'span 8' }}>
              <Stack spacing={6}>
                {/* Featured Image - smaller with subtle overlay */}
                {data.image_url && (
                  <Box borderRadius="xl" overflow="hidden" cursor="pointer" onClick={() => openLightboxAtSrc(assetUrl(data.image_url) || (data.image_url as any))}>
                    <Image src={assetUrl(data.image_url) || data.image_url} alt={data.title} w="100%" h="auto" objectFit="contain" />
                  </Box>
                )}
                {/* Match Section - Card with logos, score/countdown, venue/date */}
                {(matchLinkQuery.data as any)?.external_match_id && (
                  <Box position="relative" borderWidth="1px" borderRadius="lg" p={{ base: 4, md: 5 }} bg={cardBg} overflow="hidden" cursor="pointer"
                       onClick={() => {
                         const fm: any = facrMatchQuery?.data as any;
                         if (fm) {
                           setSelectedMatch({ ...fm, competition: fm.competitionName });
                           setIsMatchModalOpen(true);
                           return;
                         }
                         if (matchSnapshot) {
                           setSelectedMatch({
                             date_time: matchSnapshot.date_time,
                             venue: matchSnapshot.venue,
                             home: matchSnapshot.home,
                             away: matchSnapshot.away,
                             competition: matchSnapshot.competition,
                             score: (matchSnapshot.score || '')
                           } as any);
                           setIsMatchModalOpen(true);
                         }
                       }}>
                    {/* Edge gradients removed per design feedback; keep simple bordered card */}
                    <Heading as="h3" size="md" mb={3}>Zápas k článku</Heading>
                    {facrMatchQuery.isLoading ? (
                      <Text color={textMuted}>Načítám údaje o zápasu…</Text>
                    ) : facrMatchQuery.data ? (
                      <>
                        <HStack spacing={2} wrap="wrap" mb={3}>
                          {facrMatchQuery.data.competitionName && (
                            <Badge colorScheme="blue">{String(facrMatchQuery.data.competitionName)}</Badge>
                          )}
                          <Badge>{String((facrMatchQuery.data as any).date_time || (facrMatchQuery.data as any).date || '')}</Badge>
                        </HStack>
                        <Flex align="center" justify="space-between" gap={4}>
                          <VStack flex={1} spacing={2} minW="0">
                            <TeamLogo size="custom" style={{ width: 64, height: 64 }} teamId={String((facrMatchQuery.data as any).home_team_id || (facrMatchQuery.data as any).home_id || '')} teamName={String((facrMatchQuery.data as any).home || (facrMatchQuery.data as any).home_team || '')} facrLogo={(facrMatchQuery.data as any).home_logo_url} />
                            <Text fontWeight="600" noOfLines={2} textAlign="center">{String((facrMatchQuery.data as any).home || (facrMatchQuery.data as any).home_team || '')}</Text>
                          </VStack>
                          <VStack minW={{ base: '100px', md: '140px' }}>
                            {(() => {
                              const raw = String((facrMatchQuery.data as any).date_time || (facrMatchQuery.data as any).date || '');
                              const hasScore = ((facrMatchQuery.data as any).result_home != null && (facrMatchQuery.data as any).result_away != null) || Boolean((facrMatchQuery.data as any).score && (facrMatchQuery.data as any).score !== 'vs');
                              if (hasScore) {
                                const score = String((facrMatchQuery.data as any).score || `${(facrMatchQuery.data as any).result_home}:${(facrMatchQuery.data as any).result_away}`);
                                return (<Heading size="2xl">{score}</Heading>);
                              }
                              const parseFacr = (s: string): Date => {
                                const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
                                if (m) {
                                  const d = parseInt(m[1],10);
                                  const mo = parseInt(m[2],10)-1;
                                  const y = parseInt(m[3],10);
                                  const hh = m[4] ? parseInt(m[4],10) : 0;
                                  const mm = m[5] ? parseInt(m[5],10) : 0;
                                  return new Date(y, mo, d, hh, mm);
                                }
                                const t = Date.parse(s);
                                return isNaN(t) ? new Date() : new Date(t);
                              };
                              const d = parseFacr(raw);
                              const now = Date.now();
                              const ms = d.getTime() - now;
                              const days = Math.max(0, Math.floor(ms / (1000*60*60*24)));
                              const hours = Math.max(0, Math.floor((ms % (1000*60*60*24))/(1000*60*60)));
                              const mins = Math.max(0, Math.floor((ms % (1000*60*60))/(1000*60)));
                              return (<Text fontSize="lg" fontWeight="700">Za {days} d {hours} h {mins} min</Text>);
                            })()}
                            {(() => {
                              const dRaw = String((facrMatchQuery.data as any).date_time || (facrMatchQuery.data as any).date || '');
                              const m = dRaw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
                              let d: Date;
                              if (m) {
                                const dd = parseInt(m[1], 10);
                                const MM = parseInt(m[2], 10) - 1;
                                const yyyy = parseInt(m[3], 10);
                                const hh = m[4] ? parseInt(m[4], 10) : 0;
                                const mm = m[5] ? parseInt(m[5], 10) : 0;
                                d = new Date(yyyy, MM, dd, hh, mm);
                              } else {
                                const t = Date.parse(dRaw);
                                d = isNaN(t) ? new Date() : new Date(t);
                              }
                              return <Text fontSize="sm" color={textMuted}>{d.toLocaleDateString('cs-CZ')} {d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}</Text>;
                            })()}
                            {(facrMatchQuery.data as any).venue && <Text fontSize="sm" color={textMuted}>{String((facrMatchQuery.data as any).venue)}</Text>}
                          </VStack>
                          <VStack flex={1} spacing={2} minW="0">
                            <TeamLogo size="custom" style={{ width: 64, height: 64 }} teamId={String((facrMatchQuery.data as any).away_team_id || (facrMatchQuery.data as any).away_id || '')} teamName={String((facrMatchQuery.data as any).away || (facrMatchQuery.data as any).away_team || '')} facrLogo={(facrMatchQuery.data as any).away_logo_url} />
                            <Text fontWeight="600" noOfLines={2} textAlign="center">{String((facrMatchQuery.data as any).away || (facrMatchQuery.data as any).away_team || '')}</Text>
                          </VStack>
                        </Flex>
                        {(((facrMatchQuery.data as any).facr_link || (facrMatchQuery.data as any).report_url)) && (
                          <Box mt={5} pt={2} textAlign="center">
                            {(() => {
                              const raw = String(((facrMatchQuery.data as any).facr_link || (facrMatchQuery.data as any).report_url || ''));
                              const safe = raw.replace('is.fotbal.cz', 'www.fotbal.cz');
                              return (
                                <Button
                                  as={Link}
                                  href={safe}
                                  isExternal
                                  rightIcon={<ExternalLink size={16} />}
                                  size="sm"
                                  variant="link"
                                  colorScheme="blue"
                                >
                                  Zobrazit více informací na fotbal.cz
                                </Button>
                              );
                            })()}
                          </Box>
                        )}
                        <Text mt={2} fontSize="xs" color={textMuted} position="absolute" right={3} bottom={2}>
                          Data pocházejí z{' '}
                          <Link href="https://www.fotbal.cz" isExternal color="blue.600" textDecoration="underline">FAČR</Link>
                        </Text>
                      </>
                    ) : (
                      <Text color={textMuted}>Propojeno s FACR ID: {(matchLinkQuery.data as any)?.external_match_id}</Text>
                    )}
                  </Box>
                )}

                {/* Article Content - Main Section with editor-like lists */}
                <Box
                  className="article-content"
                  bg={useColorModeValue('white','gray.900')}
                  borderRadius="lg"
                  p={{ base: 4, md: 6 }}
                  ref={contentRef}
                  sx={{
                    'ul, ol': { pl: 6, listStylePosition: 'outside' },
                    'ul': { listStyleType: 'disc' },
                    'ul[data-bullets="disc"]': { listStyleType: 'disc' },
                    'ul[data-bullets="circle"]': { listStyleType: 'circle' },
                    'ul[data-bullets="square"]': { listStyleType: 'square' },
                    'ul[data-bullets="none"]': { listStyleType: 'none' },
                    'ol': { listStyleType: 'decimal' },
                    'li': { mb: 2 },
                    'a': { color: 'blue.600', textDecoration: 'underline', _hover: { color: 'blue.700' } },
                    'blockquote': {
                      borderLeft: '4px solid #3182ce',
                      paddingLeft: '16px',
                      margin: '1em 0',
                      color: useColorModeValue('#4a5568','#cbd5e0'),
                      fontStyle: 'italic',
                      backgroundColor: useColorModeValue('#f7fafc','#1a202c'),
                      padding: '12px 16px',
                      borderRadius: '4px',
                    },
                    'img': {
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      mt: 2,
                      border: 'none !important',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                      cursor: 'pointer',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: safeContentHTML }}
                />
                {articleBanners.length > 0 && (
                  <Box textAlign="center" mt={{ base: 4, md: 6 }}>
                    <a
                      href={articleBanners[0].click_url || '#'}
                      target={articleBanners[0].click_url ? '_blank' : undefined}
                      rel={articleBanners[0].click_url ? 'noopener noreferrer' : undefined}
                      style={{ display: 'inline-block' }}
                    >
                      <Image
                        src={assetUrl((articleBanners[0] as any).image_url) || '/images/sponsors/placeholder.png'}
                        alt={articleBanners[0].name}
                        maxW="100%"
                        w={articleBanners[0].width ? `${articleBanners[0].width}px` : '100%'}
                        h={articleBanners[0].height ? `${articleBanners[0].height}px` : 'auto'}
                        borderRadius="md"
                        loading="lazy"
                        decoding="async"
                      />
                    </a>
                  </Box>
                )}
                {/* YouTube Video Section - card with modal */}
                {(data as any)?.youtube_video_id && (
                  <Box borderWidth="1px" borderRadius="lg" overflow="hidden" bg={videoBg}>
                    <Box
                      role="group"
                      cursor="pointer"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsYoutubeOpen(true); umamiTrackEvent('Video Widget Shown', { id: (data as any).youtube_video_id, title: (data as any).youtube_video_title }); } }}
                      onClick={() => { setIsYoutubeOpen(true); umamiTrackEvent('Video Widget Shown', { id: (data as any).youtube_video_id, title: (data as any).youtube_video_title }); }}
                    >
                      <AspectRatio ratio={16/9}>
                        <Box position="relative">
                          <Image src={(data as any).youtube_video_thumbnail || `https://i.ytimg.com/vi/${(data as any).youtube_video_id}/hqdefault.jpg`} alt={(data as any).youtube_video_title || 'YouTube video'} objectFit="cover" />
                          <Box
                            position="absolute"
                            inset={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg="blackAlpha.500"
                            pointerEvents="none"
                            transition="background-color 0.2s ease"
                            _groupHover={{ bg: 'blackAlpha.600' }}
                          >
                            <Box
                              bg="brand.primary"
                              color="white"
                              borderRadius="full"
                              px={5}
                              py={3}
                              display="flex"
                              alignItems="center"
                              gap={2}
                              transform="scale(1)"
                              transition="transform 0.2s ease, box-shadow 0.2s ease"
                              boxShadow="0 8px 24px rgba(0,0,0,0.3)"
                              _groupHover={{ transform: 'scale(1.06)', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}
                            >
                              <Play size={18} />
                              <Text fontWeight="bold">Přehrát</Text>
                            </Box>
                          </Box>
                        </Box>
                      </AspectRatio>
                      <Box p={3}>
                        <Text fontWeight="700" color={videoTitleColor} noOfLines={2}>{(data as any).youtube_video_title || 'Video'}</Text>
                        {(data as any).published_at && (
                          <Text fontSize="sm" color={textMuted}>{new Date((data as any).published_at).toLocaleDateString('cs-CZ')}</Text>
                        )}
                        {(data as any).youtube_video_url && (
                          <Link href={(data as any).youtube_video_url} isExternal color="blue.600" onClick={(e) => { e.stopPropagation(); }}>
                            Otevřít na YouTube
                          </Link>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                {/* Gallery Section - Mosaic of 5 color images (random) */}
                {((data as any)?.gallery_album_id || (data as any)?.gallery_album_url) && (
                  <Box borderWidth="1px" borderRadius="lg" p={{ base: 3, md: 4 }} bg={galleryBg} borderColor={galleryBorder}>
                    <Box mb={3}>
                      <Heading as="h3" size="md" textAlign="center">{t('gallery.page_title')}</Heading>
                      {(() => {
                        const albumPhotos = Array.isArray(galleryAlbumQuery.data?.photos) ? (galleryAlbumQuery.data?.photos as any[]) : [];
                        if (albumPhotos.length === 0) return null;
                        const selected = gallerySelectedAndExtra.selected;
                        const extra = gallerySelectedAndExtra.extra;
                        const pool = (extra.length ? extra : selected).slice(0, 5);
                        if (pool.length === 0) return null;
                        return (
                          <Box>
                            {pool.length < 5 ? (
                              <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={2} role="group">
                                {pool.map((p: any) => (
                                  <Image key={`extra-${p.id}`} src={p.image_1500} alt={String(p.id)} w="100%" h="140px" objectFit="cover" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(p.image_1500))} />
                                ))}
                              </SimpleGrid>
                            ) : (
                              <Box position="relative" role="group" sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1.2fr 1fr',
                                gridTemplateRows: 'repeat(2, 140px)',
                                gap: '8px'
                              }}>
                                <Image src={pool[0].image_1500} alt={String(pool[0].id)} sx={{ gridColumn: 1, gridRow: 1 }} objectFit="cover" w="100%" h="100%" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(pool[0].image_1500))} />
                                <Image src={pool[1].image_1500} alt={String(pool[1].id)} sx={{ gridColumn: 1, gridRow: 2 }} objectFit="cover" w="100%" h="100%" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(pool[1].image_1500))} />
                                <Image src={pool[2].image_1500} alt={String(pool[2].id)} sx={{ gridColumn: 2, gridRow: '1 / span 2' }} objectFit="cover" w="100%" h="100%" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(pool[2].image_1500))} />
                                <Image src={pool[3].image_1500} alt={String(pool[3].id)} sx={{ gridColumn: 3, gridRow: 1 }} objectFit="cover" w="100%" h="100%" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(pool[3].image_1500))} />
                                <Image src={pool[4].image_1500} alt={String(pool[4].id)} sx={{ gridColumn: 3, gridRow: 2 }} objectFit="cover" w="100%" h="100%" borderRadius="md" cursor="pointer" onClick={() => openLightboxAtSrc(String(pool[4].image_1500))} />
                                <Button
                                  as={RouterLink}
                                  to={(data as any).gallery_album_id ? `/galerie/album/${(data as any).gallery_album_id}` : '#'}
                                  size="sm"
                                  colorScheme="blue"
                                  position="absolute"
                                  top="50%"
                                  left="50%"
                                  transform="translate(-50%, -50%)"
                                  cursor="pointer"
                                  transition="all 0.2s ease"
                                  boxShadow="sm"
                                  _hover={{ transform: 'translate(-50%, -50%) scale(1.06)', boxShadow: 'md' }}
                                  _active={{ transform: 'translate(-50%, -50%) scale(0.98)' }}
                                  _focusVisible={{ boxShadow: '0 0 0 3px var(--chakra-colors-blue-300)' }}
                                  onClick={() => umamiTrackEvent('Gallery Link Click', { album_id: (data as any).gallery_album_id })}
                                >
                                  Zobrazit celou galerii
                                </Button>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
                {/* Polls moved to sidebar */}
              </Stack>
            </Box>
            <VStack align="stretch" spacing={6} gridColumn={{ base: '1 / -1', lg: 'span 4' }}>

              {relatedArticlesQuery.isLoading ? null : (() => {
                const list = ((relatedArticlesQuery.data as any)?.data || [])
                  .filter((a: any) => a?.id !== (data as any)?.id)
                  .slice(0, 4);
                if (!list.length) return null;
                return (
                  <Widget title="Podobné články">
                    <VStack spacing={3} align="stretch">
                      {list.map((a: any) => {
                        const link = a.slug ? `/news/${a.slug}` : `/articles/${a.id}`;
                        return (
                          <HStack key={a.id} align="flex-start" spacing={3} as={RouterLink} to={link} _hover={{ textDecoration: 'none' }}>
                            <Image src={assetUrl(a.image_url) || '/stadium-placeholder.jpg'} alt={a.title} boxSize="64px" objectFit="cover" borderRadius="md" />
                            <VStack align="start" spacing={1} flex={1} minW={0}>
                              <Text fontWeight="600" noOfLines={2}>{a.title}</Text>
                              {a.published_at && (
                                <Text fontSize="sm" color={textMuted}>{new Date(a.published_at).toLocaleDateString('cs-CZ')}</Text>
                              )}
                            </VStack>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Widget>
                );
              })()}

              <MatchesWidget
                categoryName={categoryNameForMatches}
                hideEmpty
                onMatchClick={(m: any) => {
                  setSelectedMatch({ ...m, competition: (m as any).competitionName, competitionName: (m as any).competitionName });
                  setIsMatchModalOpen(true);
                }}
              />

              {(() => {
                const shouldShowMini = !categoryNameForMatches || (catUpcomingMatches || []).length === 0;
                if (!shouldShowMini) return null;
                const WEEKDAYS = ['Po','Út','St','Čt','Pá','So','Ne'];
                const todayKey = (() => { try { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; } catch { return ''; } })();
                return (
                  <Widget title="Kalendář zápasů">
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <Button size="xs" variant="outline" onClick={() => setMiniMonthRef(addMonths(miniMonthRef, -1))}>{'‹'}</Button>
                          <Text fontWeight="600">{format(miniMonthRef, 'LLLL yyyy', { locale: cs })}</Text>
                          <Button size="xs" variant="outline" onClick={() => setMiniMonthRef(new Date())}>Dnes</Button>
                          <Button size="xs" variant="outline" onClick={() => setMiniMonthRef(addMonths(miniMonthRef, 1))}>{'›'}</Button>
                        </HStack>
                      </HStack>
                      <SimpleGrid columns={7} spacing={1}>
                        {WEEKDAYS.map((w) => (
                          <Box key={w} textAlign="center" fontSize="xs" color={textMuted}>{w}</Box>
                        ))}
                        {miniWeeks.map((d, idx) => {
                          const key = format(d, 'yyyy-MM-dd');
                          const list = miniByDate.get(key) || [];
                          const faded = !isSameMonth(d, miniMonthRef);
                          const isToday = key === todayKey;
                          return (
                            <Box
                              key={idx}
                              p={1}
                              borderWidth="2px"
                              borderColor={miniExpandedKey === key ? 'blue.500' : (isToday ? 'blue.400' : 'gray.200')}
                              borderRadius="md"
                              bg={miniExpandedKey === key ? miniTodayBg : (isToday ? miniTodayBg : undefined)}
                              cursor={list.length ? 'pointer' : 'default'}
                              opacity={faded ? 0.6 : 1}
                              onClick={() => {
                                if (!list.length) return;
                                setMiniExpandedKey((prev) => prev === key ? null : key);
                              }}
                            >
                              <HStack justify="space-between">
                                <Text fontSize="xs" fontWeight="600">{format(d, 'd', { locale: cs })}</Text>
                                {!!list.length && (
                                  <Badge colorScheme="blue" borderRadius="full" fontSize="9px">{list.length}</Badge>
                                )}
                              </HStack>
                            </Box>
                          );
                        })}
                      </SimpleGrid>
                      {miniExpandedKey && (miniByDate.get(miniExpandedKey) || []).length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          {(miniByDate.get(miniExpandedKey) || []).slice(0, 5).map((m: any, i: number) => (
                            <HStack key={`${miniExpandedKey}-${i}`} spacing={2} align="center" onClick={() => {
                              const snapshot = {
                                date_time: m.date_time,
                                venue: m.venue,
                                home: m.home,
                                away: m.away,
                                competition: m.competitionName,
                                score: (m.score || ''),
                                facr_link: (m as any).facr_link,
                                report_url: (m as any).report_url,
                                home_logo_url: (m as any).home_logo_url,
                                away_logo_url: (m as any).away_logo_url,
                              } as any;
                              setSelectedMatch(snapshot);
                              setIsMatchModalOpen(true);
                            }} cursor="pointer" _hover={{ bg: miniHoverBg }} p={1} borderRadius="md">
                              <Text fontSize="xs" color={textMuted} minW="42px">{String(m.date_time || '').split(' ')[1] || '—'}</Text>
                              <TeamLogo
                                size="small"
                                teamId={String((m as any).home_id || '')}
                                teamName={String((m as any).home || '')}
                                facrLogo={(m as any).home_logo_url}
                              />
                              <Text fontSize="sm" noOfLines={1} flex={1} minW={0}>{m.home} vs {m.away}</Text>
                              <TeamLogo
                                size="small"
                                teamId={String((m as any).away_id || '')}
                                teamName={String((m as any).away || '')}
                                facrLogo={(m as any).away_logo_url}
                              />
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  </Widget>
                );
              })()}

              {(() => {
                const all = Array.isArray(upcomingEventsQuery.data) ? (upcomingEventsQuery.data as any[]) : [];
                const cat = (data as any)?.category?.name;
                const items = cat
                  ? all.filter((ev: any) => !ev?.category_name || String(ev.category_name) === String(cat)).slice(0, 3)
                  : all.slice(0, 3);
                if (!items.length) return null;
                return (
                  <Widget title="Nejbližší aktivity">
                    <VStack spacing={3} align="stretch">
                      {items.map((ev: any) => (
                        <HStack
                          key={ev.id}
                          as={RouterLink}
                          to={`/aktivita/${ev.id}`}
                          align="flex-start"
                          spacing={3}
                          px={3}
                          py={2}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor={galleryBorder}
                          bg={attachmentsBg}
                          style={{ borderLeftWidth: 4, borderLeftColor: '#3182ce' }}
                          _hover={{ textDecoration: 'none', bg: miniHoverBg, borderColor: 'blue.300', boxShadow: 'sm', transform: 'translateX(2px)' }}
                       >
                          <Box flex={1} minW={0}>
                            <Text fontWeight="700" noOfLines={2}>{ev.title}</Text>
                            <Text fontSize="sm" color={textMuted}>
                              {(() => { try { const d = new Date(ev.start_time); return d.toLocaleDateString('cs-CZ') + (ev.location ? ` • ${ev.location}` : ''); } catch { return ev.start_time; } })()}
                            </Text>
                          </Box>
                        </HStack>
                      ))}
                    </VStack>
                  </Widget>
                );
              })()}

              {/* Polls in sidebar (render only when polls exist; internal wrapper handles layout) */}
              {(data as any)?.id && (
                <EmbeddedPoll articleId={(data as any).id} maxPolls={2} showTitle={false} />
              )}

              {/* Attachments in sidebar */}
              {Array.isArray((data as any)?.attachments) && (data as any).attachments.length > 0 && (
                <Widget title="Přílohy">
                  <VStack align="stretch" spacing={2}>
                    {(data as any).attachments.map((f: any, idx: number) => (
                      <FilePreview
                        key={idx}
                        url={assetUrl(f.url) || f.url}
                        name={f.name || ''}
                        mimeType={f.mime_type || ''}
                        size={f.size}
                      />
                    ))}
                  </VStack>
                </Widget>
              )}

              {/* Sidebar banner placement */}
              {sidebarBanners.length > 0 && (
                <Box>
                  <a
                    href={sidebarBanners[0].click_url || '#'}
                    target={sidebarBanners[0].click_url ? '_blank' : undefined}
                    rel={sidebarBanners[0].click_url ? 'noopener noreferrer' : undefined}
                    style={{ display: 'block' }}
                  >
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img
                      src={assetUrl((sidebarBanners[0] as any).image_url) || '/images/sponsors/placeholder.png'}
                      alt={sidebarBanners[0].name}
                      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </Box>
              )}
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>
    {/* Comments at the end */}
    {(data as any)?.id ? (
      <Container maxW="7xl" mt={6} pb={{ base: 8, md: 12 }}>
        <CommentsSection targetType="article" targetId={String((data as any).id)} />
      </Container>
    ) : null}
    {/* Newsletter CTA */}
    <NewsletterCTA />
    <MatchModal isOpen={isMatchModalOpen} onClose={() => setIsMatchModalOpen(false)} match={selectedMatch} />
    {/* Image Lightbox */
    }
    <Modal isOpen={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
        <ModalCloseButton color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.700' }} borderRadius="full" zIndex={2} />
        <ModalBody p={0} position="relative">
          <Box position="relative" maxH="85vh" display="flex" alignItems="center" justifyContent="center">
            {lightboxImages[lightboxIndex] && (
              <Image src={lightboxImages[lightboxIndex]} alt={`Obrázek ${lightboxIndex+1}/${lightboxImages.length}`} maxH="85vh" maxW="100%" objectFit="contain" />
            )}
            {lightboxImages.length > 1 && (
              <>
                <IconButton aria-label="Předchozí" icon={<ChevronLeft />} onClick={onPrev} position="absolute" left={{ base: '4%', md: '5%' }} top="50%" transform="translateY(-50%)" colorScheme="blackAlpha" variant="solid" zIndex={3} _hover={{ bg: 'blackAlpha.700', transform: 'translateY(-50%)' }} _active={{ bg: 'blackAlpha.800', transform: 'translateY(-50%)' }} />
                <IconButton aria-label="Další" icon={<ChevronRight />} onClick={onNext} position="absolute" right={{ base: '4%', md: '5%' }} top="50%" transform="translateY(-50%)" colorScheme="blackAlpha" variant="solid" zIndex={3} _hover={{ bg: 'blackAlpha.700', transform: 'translateY(-50%)' }} _active={{ bg: 'blackAlpha.800', transform: 'translateY(-50%)' }} />
              </>
            )}
            {/* Clickable side areas to close */}
            <Box position="absolute" left={0} top={0} bottom={0} w={{ base: '6%', md: '8%' }} onClick={() => setIsLightboxOpen(false)} zIndex={1} />
            <Box position="absolute" right={0} top={0} bottom={0} w={{ base: '6%', md: '8%' }} onClick={() => setIsLightboxOpen(false)} zIndex={1} />
          </Box>
          {/* Controls under image - centered with subtle bg */}
          <VStack align="stretch" spacing={2} mt={3} px={2}>
            <Box display="flex" justifyContent="center">
              <HStack spacing={2} bg="blackAlpha.600" px={3} py={2} borderRadius="full">
                <Button
                  size="sm"
                  leftIcon={<Copy size={16} />}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(lightboxImages[lightboxIndex] || '');
                      toast({ title: 'Zkopírováno', status: 'success', duration: 1500 });
                    } catch {
                      toast({ title: 'Nepodařilo se zkopírovat', status: 'error', duration: 1500 });
                    }
                  }}
                >
                  Kopírovat odkaz
                </Button>
                <Button
                  as={Link}
                  href={lightboxImages[lightboxIndex] || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  leftIcon={<ExternalLink size={16} />}
                >
                  Otevřít originál
                </Button>
              </HStack>
            </Box>
            
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
    {/* YouTube modal */}
    <Modal isOpen={isYoutubeOpen} onClose={() => setIsYoutubeOpen(false)} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="black" maxW="90vw">
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />
        <ModalBody p={0}>
          {(data as any)?.youtube_video_id && (
            <AspectRatio ratio={16/9}>
              <Box
                as="iframe"
                src={`https://www.youtube-nocookie.com/embed/${(data as any).youtube_video_id}?autoplay=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                title={(data as any).youtube_video_title || 'YouTube video'}
                onLoad={() => umamiTrackVideoPlay((data as any).youtube_video_id, (data as any).youtube_video_title)}
              />
            </AspectRatio>
          )}
          {(data as any)?.youtube_video_id && (
            <Box bg={youtubeCommentsBg} p={4}>
              <CommentsSection targetType="youtube_video" targetId={(data as any).youtube_video_id} />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  </MainLayout>
);
};

export default ArticleDetailPage;
